import { Sidebar } from '@components/dashboard/superuser/Sidebar';
import { INVENTORY_SIDEBAR_SECTIONS } from '@components/dashboard/superuser/sidebarConfig';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { AccessControlCode } from '@shared/constants';
import {
  selectCurrentUser,
  selectHasAccessControl,
} from '@store/auth/authSelectors';
import {
  selectAllOutlets,
  selectEffectiveOutletId,
  setSelectedOutletId,
} from '@store/outlets';
import { Package2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { OutletSelector } from '../components/common';
import { UserActionsMenu } from '../components/nav/UserActionsMenu';

export const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const outletId = useAppSelector(selectEffectiveOutletId);
  const outlets = useAppSelector(selectAllOutlets);
  const canAccess = useAppSelector(
    selectHasAccessControl(AccessControlCode.DASHBOARD_PAGE_ACCESS),
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (!outletId && outlets.length > 0) {
      dispatch(setSelectedOutletId(outlets[0].id));
    }
  }, [dispatch, outletId, outlets]);

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (!canAccess) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Package2 size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-600">Access restricted</p>
          <p className="mt-1 text-xs text-gray-400">
            You do not have permission to access inventory.
          </p>
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="mt-4 text-xs text-pos-primary underline"
          >
            Back to sales
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans text-sm text-gray-900">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
        sections={INVENTORY_SIDEBAR_SECTIONS}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-gray-100 bg-white px-8 py-4">
          <div className="flex items-center gap-4">
            <Package2 size={32} className="text-pos-primary" />
            <div className="flex-1">
              <h1 className="text-base font-semibold text-gray-900">
                Inventory
              </h1>
              <p className="text-xs text-gray-400">
                Stock, suppliers, purchasing, and recipe management workspace.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <OutletSelector />
            <UserActionsMenu />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default InventoryPage;
